import React, { useEffect, useState } from "react";
import "./index.css";

// Types Alert

export type FoodAlertTagType = {
  id: number;
  name: string;
  img: string;
};

type FoodAlertTagsProps = {
  tags?: FoodAlertTagType[];
  variant?: "compact" | "detail";
};

const COLLAPSED_MAX_VISIBLE = 4;

function resolveTagImage(img: string) {
  if (!img) return "";
  if (img.startsWith("http")) return img;
  if (process.env.REACT_APP_AWS_S3_URL) {
    return `${process.env.REACT_APP_AWS_S3_URL}/${img}`;
  }
  return img;
}

function ChevronIcon({ direction }: { direction: "down" | "up" }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={
        direction === "up"
          ? "food-alert-tag-chevron food-alert-tag-chevron-up"
          : "food-alert-tag-chevron food-alert-tag-chevron-down"
      }
    >
      <path
        d="M3.5 5.25L7 8.75L10.5 5.25"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FoodAlertTags({
  tags = [],
  variant = "compact",
}: FoodAlertTagsProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const isDetail = variant === "detail";
  const isInteractive = isDetail;
  const hasMoreTags = tags.length > COLLAPSED_MAX_VISIBLE;

  const visibleTags =
    !hasMoreTags || (isDetail && isExpanded)
      ? tags
      : tags.slice(0, COLLAPSED_MAX_VISIBLE);

  useEffect(() => {
    if (!selectedImage) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage]);

  if (!tags.length) return null;

  const openTagImage = (
    event: React.MouseEvent<HTMLButtonElement>,
    img: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const imageUrl = resolveTagImage(img);
    if (!imageUrl) return;
    setSelectedImage(imageUrl);
  };

  const closeModal = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setSelectedImage(null);
  };

  const toggleExpanded = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded((current) => !current);
  };

  return (
    <>
      <div
        className={`food-alert-tags ${
          isDetail ? "food-alert-tags-detail" : "food-alert-tags-compact"
        } ${isDetail && isExpanded ? "food-alert-tags-expanded" : ""}`}
      >
        {visibleTags.map((tag) =>
          isInteractive ? (
            <button
              key={tag.id}
              type="button"
              className="food-alert-tag-item food-alert-tag-item-interactive"
              aria-label="Ver informação alimentar"
              onClick={(event) => openTagImage(event, tag.img)}
            >
              <img src={resolveTagImage(tag.img)} alt="" draggable={false} />
            </button>
          ) : (
            <span key={tag.id} className="food-alert-tag-item">
              <img src={resolveTagImage(tag.img)} alt="" draggable={false} />
            </span>
          ),
        )}

        {!isDetail && hasMoreTags && (
          <span className="food-alert-tag-more" aria-hidden="true">
            ...
          </span>
        )}

        {isDetail && hasMoreTags && (
          <button
            type="button"
            className="food-alert-tag-toggle food-alert-tag-toggle-arrow"
            aria-label={
              isExpanded
                ? "Recolher informações alimentares"
                : "Expandir informações alimentares"
            }
            aria-expanded={isExpanded}
            onClick={toggleExpanded}
          >
            <ChevronIcon direction={isExpanded ? "up" : "down"} />
          </button>
        )}
      </div>

      {isInteractive && selectedImage && (
        <div
          className="food-alert-tag-modal-overlay"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="food-alert-tag-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Informação alimentar"
          >
            <button
              type="button"
              className="food-alert-tag-modal-close"
              aria-label="Fechar"
              onClick={closeModal}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.978551 2.97126L6.957 8.94971L0.978551 14.9282L2.97137 16.921L8.94981 10.9425L14.9283 16.921L16.9211 14.9282L10.9426 8.94971L16.9211 2.97126L14.9283 0.978446L8.94981 6.95689L2.97137 0.978446L0.978551 2.97126Z"
                  fill="#636363"
                />
              </svg>
            </button>
            <div className="food-alert-tag-modal-image-wrap">
              <img src={selectedImage} alt="" draggable={false} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
